import { useEffect, useState } from "react";
import { api, notifyError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CURRENCY_OPTIONS } from "../lib/currency";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import WhatsAppIcon from "../components/WhatsAppIcon";
import HelpVideosButton from "../components/HelpVideosDialog";
import {
  Loader2,
  Settings as SettingsIcon,
  Trash2,
  Printer,
  Boxes,
  Coins,
  Sparkles,
  Warehouse,
  Plus,
  Pencil,
  Star,
  LifeBuoy,
  Globe,
  Mail,
  Phone,
  MapPin,
  Check,
  X,
} from "lucide-react";

export default function Settings() {
  const {
    tenantSettings,
    defaultCurrency,
    updateTenantSettings,
    refreshTenantSettings,
  } = useAuth();
  const [currency, setCurrency] = useState(defaultCurrency);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Çıktılarda görünecek iletişim bilgileri (makbuz + etiketler)
  const emptyContact = { receipt_phone: "", receipt_address: "", receipt_website: "", receipt_email: "" };
  const [contact, setContact] = useState(emptyContact);
  const [contactLoaded, setContactLoaded] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);

  // Warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [whLoading, setWhLoading] = useState(true);
  const [whDialog, setWhDialog] = useState({ open: false, mode: "create", item: null });
  const [whName, setWhName] = useState("");
  const [whCapacity, setWhCapacity] = useState(100);
  const [whSaving, setWhSaving] = useState(false);
  const [whDelete, setWhDelete] = useState(null);
  const [whDeleting, setWhDeleting] = useState(false);
  const [defaultSaving, setDefaultSaving] = useState(null);

  // Empty (previously-used, now free) shelf locations per warehouse
  const [locDialog, setLocDialog] = useState({ open: false, warehouse: null });
  const [freeLocs, setFreeLocs] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [editLocValue, setEditLocValue] = useState("");
  const [renamingLoc, setRenamingLoc] = useState(false);
  const [confirmDeleteLoc, setConfirmDeleteLoc] = useState(null);
  const [deletingLoc, setDeletingLoc] = useState(null);

  const loadFreeLocs = async (wid) => {
    setLocLoading(true);
    try {
      const { data } = await api.get(`/warehouses/${wid}/free-locations`);
      setFreeLocs(data.locations || []);
    } catch (e) {
      notifyError(e);
    } finally {
      setLocLoading(false);
    }
  };

  const openLocations = async (w) => {
    setEditingLoc(null);
    setFreeLocs([]);
    setLocDialog({ open: true, warehouse: w });
    await loadFreeLocs(w.id);
  };

  const startEditLoc = (loc) => {
    setEditingLoc(loc);
    setEditLocValue(loc);
  };

  const saveRenameLoc = async () => {
    const wid = locDialog.warehouse?.id;
    const oldName = editingLoc;
    const newName = editLocValue.trim();
    if (!wid || !oldName) return;
    if (!newName) {
      toast.error("Yeni ad girin");
      return;
    }
    setRenamingLoc(true);
    try {
      await api.put(`/warehouses/${wid}/locations/rename`, {
        old_name: oldName,
        new_name: newName,
      });
      toast.success("Konum adı güncellendi");
      setEditingLoc(null);
      await loadFreeLocs(wid);
    } catch (e) {
      notifyError(e);
    } finally {
      setRenamingLoc(false);
    }
  };

  const deleteLoc = async (loc) => {
    const wid = locDialog.warehouse?.id;
    if (!wid) return;
    setDeletingLoc(loc);
    try {
      await api.delete(
        `/warehouses/${wid}/locations?name=${encodeURIComponent(loc)}`
      );
      toast.success("Konum silindi");
      setConfirmDeleteLoc(null);
      await loadFreeLocs(wid);
    } catch (e) {
      // Kullanımdaki konumlarda backend 400 + açıklama döner.
      notifyError(e);
    } finally {
      setDeletingLoc(null);
    }
  };

  const loadWarehouses = async () => {
    setWhLoading(true);
    try {
      const { data } = await api.get("/warehouses");
      setWarehouses(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setWhLoading(false);
    }
  };

  // Ayarlar formu HAM değerleri gösterir (fallback uygulanmamış) → kullanıcı
  // alanın ayarlı olup olmadığını görebilir.
  const loadContact = async () => {
    try {
      const { data } = await api.get("/me/tenant-settings");
      setContact({
        receipt_phone: data.receipt_phone || "",
        receipt_address: data.receipt_address || "",
        receipt_website: data.receipt_website || "",
        receipt_email: data.receipt_email || "",
      });
    } catch (e) {
      notifyError(e);
    } finally {
      setContactLoaded(true);
    }
  };

  const saveContact = async () => {
    setContactSaving(true);
    try {
      // Boş dize gönderilir → backend alanı temizler ve fallback devreye girer.
      await updateTenantSettings({
        receipt_phone: contact.receipt_phone.trim(),
        receipt_address: contact.receipt_address.trim(),
        receipt_website: contact.receipt_website.trim(),
        receipt_email: contact.receipt_email.trim(),
      });
      toast.success("İletişim bilgileri güncellendi");
      await refreshTenantSettings();
    } catch (e) {
      notifyError(e);
    } finally {
      setContactSaving(false);
    }
  };

  useEffect(() => {
    loadWarehouses();
    loadContact();
  }, []);

  const saveCurrency = async () => {
    setSaving(true);
    try {
      await updateTenantSettings({ default_currency: currency });
      toast.success("Para birimi güncellendi");
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const clearDemo = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/me/demo-data");
      const total =
        (data?.deleted?.customers || 0) +
        (data?.deleted?.storages || 0) +
        (data?.deleted?.payments || 0);
      toast.success(`Demo veriler silindi (${total} kayıt)`);
      await refreshTenantSettings();
      setConfirmClear(false);
    } catch (e) {
      notifyError(e);
    } finally {
      setClearing(false);
    }
  };

  const openCreateWh = () => {
    setWhName("");
    setWhCapacity(100);
    setWhDialog({ open: true, mode: "create", item: null });
  };

  const openEditWh = (w) => {
    setWhName(w.name);
    setWhCapacity(w.capacity);
    setWhDialog({ open: true, mode: "edit", item: w });
  };

  const submitWh = async (e) => {
    e.preventDefault();
    const name = whName.trim();
    if (!name) {
      toast.error("Depo adı girin");
      return;
    }
    const capacity = Math.max(0, Math.floor(Number(whCapacity) || 0));
    setWhSaving(true);
    try {
      if (whDialog.mode === "create") {
        await api.post("/warehouses", { name, capacity });
        toast.success("Depo eklendi");
      } else {
        await api.put(`/warehouses/${whDialog.item.id}`, { name, capacity });
        toast.success("Depo güncellendi");
      }
      setWhDialog({ open: false, mode: "create", item: null });
      await loadWarehouses();
      await refreshTenantSettings();
    } catch (e) {
      notifyError(e);
    } finally {
      setWhSaving(false);
    }
  };

  /**
   * Varsayılan depo ataması (şartname #9).
   * kind = "emanet" → Emanetler için varsayılan
   * kind = "stok"   → Stok/Satış için varsayılan
   * İkisi birbirinden bağımsızdır; aynı depo her ikisi için de seçilebilir.
   */
  const setDefault = async (w, kind = "emanet") => {
    setDefaultSaving(`${w.id}:${kind}`);
    try {
      const path = kind === "stok" ? "default-stock" : "default";
      await api.post(`/warehouses/${w.id}/${path}`);
      toast.success(
        kind === "stok"
          ? `Stok/Satış varsayılan deposu: ${w.name}`
          : `Emanet varsayılan deposu: ${w.name}`
      );
      await loadWarehouses();
      await refreshTenantSettings();
    } catch (e) {
      notifyError(e);
    } finally {
      setDefaultSaving(null);
    }
  };

  const confirmDeleteWh = async () => {
    if (!whDelete) return;
    setWhDeleting(true);
    try {
      await api.delete(`/warehouses/${whDelete.id}`);
      toast.success("Depo silindi");
      setWhDelete(null);
      await loadWarehouses();
      await refreshTenantSettings();
    } catch (e) {
      notifyError(e);
    } finally {
      setWhDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <div className="label-eyebrow text-amber-600 flex items-center gap-2">
          <SettingsIcon className="h-3.5 w-3.5" /> İşletme Ayarları
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Ayarlar</h1>
        <p className="text-sm text-muted-foreground mt-2">
          İşletmenize özel tercihleri buradan yönetin.
        </p>
      </div>

      {/* Currency */}
      <Card className="rounded-sm border-border p-6 space-y-4" data-testid="currency-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-amber-500 rounded-sm flex items-center justify-center shrink-0">
            <Coins className="h-5 w-5 text-foreground" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">Para Birimi</div>
            <p className="text-sm text-muted-foreground mt-1">
              Müşteri ödemeleri, raporlar ve emanet ücretleri bu para biriminde gösterilir.
              Daha önce kaydedilmiş ödemeler kendi para birimleriyle korunur.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end pt-2">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Varsayılan Para Birimi</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="rounded-sm" data-testid="settings-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`currency-opt-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={saveCurrency}
            disabled={saving || currency === defaultCurrency}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="save-currency-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
          </Button>
        </div>
      </Card>

      {/* Çıktı iletişim bilgileri */}
      <Card className="rounded-sm border-border p-6 space-y-4" data-testid="output-contact-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-foreground rounded-sm flex items-center justify-center shrink-0">
            <Printer className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">
              Çıktılarda Görünecek İletişim Bilgileri
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Emanet makbuzu, teslim makbuzu ve etiketlerde gösterilir. Boş
              bıraktığınız alanlar için mevcut işletme/yönetici bilgileri
              kullanılmaya devam eder.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Telefon</Label>
            <Input
              value={contact.receipt_phone}
              onChange={(e) => setContact({ ...contact, receipt_phone: e.target.value })}
              placeholder="+90 5XX XXX XX XX"
              className="rounded-sm font-mono"
              data-testid="contact-phone"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">E-posta</Label>
            <Input
              type="email"
              value={contact.receipt_email}
              onChange={(e) => setContact({ ...contact, receipt_email: e.target.value })}
              placeholder="info@ornek.com"
              className="rounded-sm"
              data-testid="contact-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Web Sitesi</Label>
            <Input
              value={contact.receipt_website}
              onChange={(e) => setContact({ ...contact, receipt_website: e.target.value })}
              placeholder="www.ornek.com"
              className="rounded-sm"
              data-testid="contact-website"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Adres</Label>
            <Input
              value={contact.receipt_address}
              onChange={(e) => setContact({ ...contact, receipt_address: e.target.value })}
              placeholder="Mahalle, Cadde No, İlçe/İl"
              className="rounded-sm"
              data-testid="contact-address"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={saveContact}
            disabled={contactSaving || !contactLoaded}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="save-contact-btn"
          >
            {contactSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
          </Button>
        </div>
      </Card>

      {/* Warehouses */}
      <Card className="rounded-sm border-border p-6 space-y-4" data-testid="warehouses-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-foreground rounded-sm flex items-center justify-center shrink-0">
            <Warehouse className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">Depolar</div>
            <p className="text-sm text-muted-foreground mt-1">
              Depo adlarını ve kapasitelerini yönetin. İki ayrı varsayılan depo
              belirleyebilirsiniz: <b>Emanetler</b> için ve <b>Stok/Satış</b> için.
              Yeni kayıt açarken ilgili varsayılan depo otomatik seçili gelir,
              istediğinizde değiştirebilirsiniz. İçinde aktif lastiği veya
              satılabilir stoğu olan depo silinemez.
            </p>
          </div>
          <Button
            onClick={openCreateWh}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="add-warehouse-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Yeni Depo
          </Button>
        </div>

        {whLoading ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : warehouses.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Henüz depo yok.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-sm">
            {warehouses.map((w) => {
              const usedPct = w.capacity > 0 ? Math.min(100, (w.used_quantity / w.capacity) * 100) : 0;
              return (
                <div
                  key={w.id}
                  className="flex flex-wrap items-center gap-3 p-3"
                  data-testid={`warehouse-row-${w.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-base tracking-tight">{w.name}</span>
                      {w.is_default && (
                        <Badge className="rounded-sm bg-amber-500 text-white hover:bg-amber-500 text-[10px]">
                          <Star className="h-3 w-3 mr-1" /> EMANET VARSAYILAN
                        </Badge>
                      )}
                      {w.is_default_stock && (
                        <Badge
                          className="rounded-sm bg-sky-600 text-white hover:bg-sky-600 text-[10px]"
                          data-testid={`wh-stock-default-badge-${w.id}`}
                        >
                          <Boxes className="h-3 w-3 mr-1" /> STOK/SATIŞ VARSAYILAN
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      {w.used_quantity} / {w.capacity} adet
                      {w.used_storages > 0 && ` • ${w.used_storages} aktif emanet`}
                      {w.stock_quantity > 0 && ` • ${w.stock_quantity} adet satılabilir stok`}
                    </div>
                    <div className="h-1.5 w-full bg-muted rounded-sm mt-2 overflow-hidden">
                      <div
                        className={`h-full ${usedPct >= 90 ? "bg-red-500" : usedPct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                        style={{ width: `${usedPct}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {!w.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => setDefault(w, "emanet")}
                        disabled={defaultSaving === `${w.id}:emanet`}
                        title="Emanetler için varsayılan depo"
                        data-testid={`set-default-${w.id}`}
                      >
                        {defaultSaving === `${w.id}:emanet` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Star className="h-3.5 w-3.5 mr-1" /> Emanet Varsayılanı</>}
                      </Button>
                    )}
                    {!w.is_default_stock && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => setDefault(w, "stok")}
                        disabled={defaultSaving === `${w.id}:stok`}
                        title="Stok/Satış için varsayılan depo"
                        data-testid={`set-default-stock-${w.id}`}
                      >
                        {defaultSaving === `${w.id}:stok` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Boxes className="h-3.5 w-3.5 mr-1" /> Stok Varsayılanı</>}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => openLocations(w)}
                      data-testid={`locations-warehouse-${w.id}`}
                    >
                      <MapPin className="h-3.5 w-3.5 mr-1" /> Boş Yerler
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => openEditWh(w)}
                      data-testid={`edit-warehouse-${w.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={() => setWhDelete(w)}
                      disabled={w.used_storages > 0 || w.stock_quantity > 0}
                      title={
                        w.used_storages > 0
                          ? "Aktif emanet varken silinemez"
                          : w.stock_quantity > 0
                          ? "Satılabilir stok varken silinemez"
                          : ""
                      }
                      data-testid={`delete-warehouse-${w.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Demo data */}
      {tenantSettings?.has_demo_data && (
        <Card className="rounded-sm border-amber-500/40 bg-amber-50/40 p-6 space-y-4" data-testid="demo-section">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 bg-foreground rounded-sm flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg tracking-tight">Demo Veriler</div>
              <p className="text-sm text-muted-foreground mt-1">
                İşletmeniz oluşturulduğunda örnek müşteriler ve emanet kayıtları eklendi.
                Gerçek verilerinizi girmeye hazır olduğunuzda bu örnekleri tek tıkla silebilirsiniz.
              </p>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmClear(true)}
              className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid="clear-demo-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Demo Verileri Temizle
            </Button>
          </div>
        </Card>
      )}

      {/* Support info */}
      <Card className="rounded-sm border-border p-6" data-testid="support-info">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-amber-500 rounded-sm flex items-center justify-center shrink-0">
            <LifeBuoy className="h-5 w-5 text-foreground" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">Bilgi ve Destek için</div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <a
                href="https://www.esnekyazilim.com.tr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold hover:text-amber-600 transition-colors"
              >
                <Globe className="h-4 w-4 text-amber-600" />
                www.esnekyazilim.com.tr
              </a>
              <a
                href="mailto:info@esnekyazilim.com.tr"
                className="inline-flex items-center gap-2 font-mono hover:text-amber-600 transition-colors"
              >
                <Mail className="h-4 w-4 text-amber-600" />
                info@esnekyazilim.com.tr
              </a>
              <a
                href="tel:+905317932454"
                className="inline-flex items-center gap-2 font-mono hover:text-amber-600 transition-colors"
              >
                <Phone className="h-4 w-4 text-amber-600" />
                +90 531 793 24 54
              </a>
              <a
                href="https://wa.me/905317932454"
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp ile yaz"
                className="inline-flex items-center gap-2 font-mono text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                <WhatsAppIcon className="h-4 w-4" />
                +90 531 793 24 54
              </a>
              {/* Yardım videoları — Pano'daki kartla aynı bileşen */}
              <HelpVideosButton />
            </div>
          </div>
        </div>
      </Card>

      {/* Warehouse create/edit dialog */}
      <Dialog
        open={whDialog.open}
        onOpenChange={(o) => setWhDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {whDialog.mode === "create" ? "Yeni Depo" : "Depo Düzenle"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitWh} className="space-y-4" data-testid="warehouse-form">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Depo Adı *</Label>
              <Input
                value={whName}
                onChange={(e) => setWhName(e.target.value)}
                placeholder="Depo1"
                className="rounded-sm"
                data-testid="wh-name"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Kapasite (adet) *</Label>
              <Input
                type="number"
                min="0"
                value={whCapacity}
                onChange={(e) => setWhCapacity(e.target.value)}
                className="rounded-sm font-mono"
                data-testid="wh-capacity"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setWhDialog({ open: false, mode: "create", item: null })}
                className="rounded-sm"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={whSaving}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="wh-submit"
              >
                {whSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDeleteLoc}
        onOpenChange={(o) => !o && setConfirmDeleteLoc(null)}
      >
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Konum silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <b className="font-mono">{confirmDeleteLoc}</b> konumu bu deponun
              geçmiş kayıtlarından temizlenecek ve "Boş Yerler" listesinden
              kalkacak. Emanet kayıtları silinmez, yalnızca raf bilgileri boşalır.
              Konumda aktif emanet varsa işlem reddedilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(ev) => {
                ev.preventDefault();
                deleteLoc(confirmDeleteLoc);
              }}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-loc"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Warehouse free-locations dialog */}
      <Dialog
        open={locDialog.open}
        onOpenChange={(o) => setLocDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Boş Yerler — {locDialog.warehouse?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Daha önce kullanılmış fakat şu anda boş olan (aktif emanedi olmayan) raf/konumlar.
            İsmini değiştirebilirsiniz; değişiklik bu depodaki geçmiş kayıtlara da yansır.
          </p>
          <div className="max-h-80 overflow-y-auto" data-testid="free-locs-list">
            {locLoading ? (
              <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : freeLocs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Boş yer bulunamadı.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-sm">
                {freeLocs.map((loc) => (
                  <div key={loc} className="flex items-center gap-2 p-2" data-testid={`free-loc-${loc}`}>
                    {editingLoc === loc ? (
                      <>
                        <Input
                          value={editLocValue}
                          onChange={(e) => setEditLocValue(e.target.value)}
                          className="rounded-sm font-mono h-9"
                          autoFocus
                          data-testid="free-loc-input"
                        />
                        <Button
                          size="sm"
                          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                          onClick={saveRenameLoc}
                          disabled={renamingLoc}
                          data-testid="free-loc-save"
                        >
                          {renamingLoc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm"
                          onClick={() => setEditingLoc(null)}
                          disabled={renamingLoc}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 font-mono text-sm">{loc}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm"
                          onClick={() => startEditLoc(loc)}
                          data-testid={`free-loc-edit-${loc}`}
                          aria-label="Adı değiştir"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                          onClick={() => setConfirmDeleteLoc(loc)}
                          disabled={deletingLoc === loc}
                          data-testid={`free-loc-delete-${loc}`}
                          aria-label="Sil"
                        >
                          {deletingLoc === loc ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocDialog({ open: false, warehouse: null })}
              className="rounded-sm"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse delete confirm */}
      <AlertDialog open={!!whDelete} onOpenChange={(o) => !o && setWhDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              "{whDelete?.name}" silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {whDelete?.used_storages > 0 ? (
                <>Bu depoda {whDelete.used_storages} aktif emanet var. Önce taşıyın veya teslim edin.</>
              ) : whDelete?.stock_quantity > 0 ? (
                <>
                  Bu depoda {whDelete.stock_quantity} adet satılabilir stok var. Önce stok
                  çıkışı yapın veya ürünleri başka depoya taşıyın.
                </>
              ) : (
                <>
                  Depo silinecek. Daha önce bu depoda kaydedilmiş <b>teslim edilmiş</b> emanetler
                  geçmişte "Tanımsız depo" olarak listelenir; raporlama hatasız çalışmaya devam eder.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteWh(); }}
              disabled={
                whDeleting ||
                (whDelete?.used_storages || 0) > 0 ||
                (whDelete?.stock_quantity || 0) > 0
              }
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="wh-confirm-delete"
            >
              {whDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Demo verileri silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Tüm örnek müşteri, emanet, hareket ve ödeme kayıtları kalıcı olarak silinecek.
              Kendi girdiğiniz veriler etkilenmez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-clear-btn">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearDemo}
              disabled={clearing}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-clear-btn"
            >
              {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
